import { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';

import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebContainer } from '@webcontainer/api';
import { useWebContainer } from '@/hooks/useWebContainer';
import { usePreviewLock } from '@/hooks/usePreviewLock';
import { ErrorBanner, type ErrorInfo } from './ErrorBanner';
import { DeployStatusPopover } from './DeployStatusPopover';
import { RotateCw, Terminal as TerminalIcon, Monitor, Code2, FileCode, FileText, Braces, Folder, FolderOpen, ChevronRight, AlertTriangle, ChevronDown, Smartphone, Tablet } from 'lucide-react';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';
import { hashDependencies, isCached, markCached } from '@/lib/depsCache';
import { CONFIG_FILES, INITIAL_SRC_FILES, SHADCN_COMPONENTS } from '@/lib/webcontainer-assets';
import { type ElementInfo, SELECTION_SCRIPT } from '@/lib/visualEdit';
import 'xterm/css/xterm.css';
import { decouperSurEtEt, enBinaireEtArguments, nomsDePaquets } from '@/lib/shellCommands';

interface WebContainerPreviewProps {
    files: Record<string, string>;
    dependencies?: Record<string, string>;
    shellActions?: string[]; // Shell commands to execute (npm install, npx)
    deletedFiles?: string[]; // Files to delete
    isLoading: boolean; // Is the AI generating code?
    isGenerating?: boolean; // Is the AI actively streaming code?
    onBuildError?: (error: string) => void; // Callback when build error occurs
    // Visual Edit props
    selectionMode?: boolean; // Enable element selection in preview
    onElementSelected?: (element: ElementInfo) => void; // Callback when element is selected
    // Multi-tab coordination
    projectId?: string; // Used for multi-tab lock coordination
    projectName?: string; // For deployment
    // Error overlay props
    pendingError?: ErrorInfo | null;
    onTryFix?: () => void;
    onDismissError?: () => void;
    isFixingError?: boolean;
}

type TabState = 'preview' | 'terminal' | 'code';
type ViewMode = 'desktop' | 'tablet' | 'mobile';

// Étapes du pipeline de génération, mappées sur les états existants
// (analyse → scaffold → dépendances → build → préview).
const PIPELINE_STEPS = ['Analyse', 'Scaffold', 'Dépendances', 'Build', 'Préview'] as const;

// Custom error class for shell command failures (like bolt.diy)
class ActionCommandError extends Error {
    readonly output: string;
    readonly header: string;

    constructor(message: string, output: string) {
        super(`${message}\n\nOutput:\n${output}`);
        this.name = 'ActionCommandError';
        this.header = message;
        this.output = output;
    }
}

// Pattern to detect Vite/TypeScript build errors and runtime issues
const ERROR_PATTERNS = [
    /error TS\d+:/i,
    /SyntaxError:/i,
    /ReferenceError:/i,
    /TypeError:/i,
    /\[vite\].*error/i,
    /Failed to resolve import/i,
    /Module not found/i,
    /Cannot find module/i,
    /Unexpected token/i,
    /is not defined/i,
    // Runtime errors that may cause white page
    /Uncaught.*Error/i,
    /React\.render.*null/i,
    /Invalid hook call/i,
    /Error: Minified React error/i,
    /Nothing was returned from render/i,
    /Target container is not a DOM element/i,
    /Cannot read propert/i,
    /undefined is not an object/i,
    /null is not an object/i,
];

function WebContainerPreviewComponent({ files, dependencies, shellActions = [], deletedFiles = [], isLoading: isAiLoading, isGenerating = false, onBuildError, selectionMode = false, onElementSelected, projectId, projectName, pendingError, onTryFix, onDismissError, isFixingError }: WebContainerPreviewProps) {
    const { webContainer, isLoading: isBooting, error: bootError } = useWebContainer();
    const { isLocked, hasPreviewLock, claimLock } = usePreviewLock(projectId);
    const [activeTab, setActiveTab] = useState<TabState>('preview');
    const [viewMode, setViewMode] = useState<ViewMode>('desktop');
    const [url, setUrl] = useState<string | null>(null);
    const urlRef = useRef<string | null>(null); // Ref to avoid stale closure in WritableStream
    const [isInstalling, setIsInstalling] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [terminalInstance, setTerminalInstance] = useState<Terminal | null>(null);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [buildError, setBuildError] = useState<string | null>(null);
    const [isWhitePage, setIsWhitePage] = useState(false);
    const [isServerReady, setIsServerReady] = useState(false); // Track when server is ready
    const whitePageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const debounceFilesRef = useRef<NodeJS.Timeout | null>(null); // Debounce HMR updates
    const executedCommandsRef = useRef<Set<string>>(new Set()); // Track already executed shell commands

    // URL bar state
    const [urlBarValue, setUrlBarValue] = useState<string>('/');
    const [currentPath, setCurrentPath] = useState<string>('/');
    const [pagesDropdownOpen, setPagesDropdownOpen] = useState(false);

    // Detect pages from generated files
    const detectedPages = useMemo(() => {
        const pages: { name: string; path: string }[] = [{ name: 'Home', path: '/' }];

        // Look for pages in src/pages/ folder
        Object.keys(files).forEach(filePath => {
            const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;

            // Check for src/pages/*.tsx files
            if (cleanPath.startsWith('src/pages/') || cleanPath.startsWith('pages/')) {
                const fileName = cleanPath.split('/').pop()?.replace(/\.tsx?$/, '') || '';
                if (fileName && !['index', 'Index', '_app', '_document'].includes(fileName)) {
                    // Convert filename to route path
                    let routePath = fileName
                        .replace(/Page$/, '') // Remove 'Page' suffix
                        .replace(/([a-z])([A-Z])/g, '$1-$2') // camelCase to kebab-case
                        .toLowerCase();

                    // Handle HomePage -> / (already added)
                    if (routePath === 'home') return;

                    pages.push({
                        name: fileName.replace(/Page$/, '').replace(/([a-z])([A-Z])/g, '$1 $2'),
                        path: '/' + routePath
                    });
                }
            }
        });

        // Also try to extract routes from App.tsx
        const appContent = files['/App.tsx'] || files['App.tsx'] || files['src/App.tsx'] || '';
        const routeMatches = appContent.matchAll(/path=["'](\/[^"']*)["']/g);
        for (const match of routeMatches) {
            const routePath = match[1];
            if (routePath && routePath !== '/' && routePath !== '*' && !pages.find(p => p.path === routePath)) {
                const name = routePath
                    .replace(/^\//g, '')
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase()) || 'Page';
                pages.push({ name, path: routePath });
            }
        }

        return pages;
    }, [files]);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const terminalRef = useRef<HTMLDivElement>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);

    // Keep track of mounted files to avoid unnecessary writes
    const mountedFilesRef = useRef<string>('');

    // Buffer for capturing error output
    const errorBufferRef = useRef<string>('');
    const errorReportedRef = useRef<boolean>(false);

    // Refs for state tracking to avoid re-renders and race conditions
    const serverStartedRef = useRef(false);
    const serverReadyListenerRef = useRef(false);

    // Reset refs when component mounts (e.g., when returning to a project)
    useEffect(() => {
        // Reset all server tracking refs on mount
        serverStartedRef.current = false;
        serverReadyListenerRef.current = false;
        urlRef.current = null; // Clear stale URL from previous project
        setUrl(null);
        setIsStarting(false);
        setIsInstalling(false);
        setBuildError(null);
        setIsWhitePage(false);
        setIsServerReady(false); // Reset server ready state
        errorBufferRef.current = '';
        errorReportedRef.current = false;

        // Cleanup timeouts on unmount
        return () => {
            if (whitePageTimeoutRef.current) {
                clearTimeout(whitePageTimeoutRef.current);
            }
            if (debounceFilesRef.current) {
                clearTimeout(debounceFilesRef.current);
            }
        };
    }, []);

    // Auto-select latest file during generation for live feedback
    const prevFilesCountRef = useRef(0);
    useEffect(() => {
        const fileKeys = Object.keys(files);
        // If new files were added during generation, auto-select the latest
        if (isAiLoading && fileKeys.length > prevFilesCountRef.current && fileKeys.length > 0) {
            const latestFile = fileKeys[fileKeys.length - 1];
            setSelectedFile(latestFile);
        }
        prevFilesCountRef.current = fileKeys.length;
    }, [files, isAiLoading]);

    // Visual Edit: Handle postMessage communication with iframe
    useEffect(() => {
        if (!selectionMode && !onElementSelected) return;

        const handleMessage = (event: MessageEvent) => {
            // Validate message origin (should come from our iframe)
            if (event.data?.type === 'PIXEL_ELEMENT_SELECTED' && onElementSelected) {
                console.log('[VisualEdit] Element selected:', event.data.data);
                onElementSelected(event.data.data);
            }
            if (event.data?.type === 'PIXEL_SELECTION_READY') {
                console.log('[VisualEdit] Selection script ready in iframe');
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [selectionMode, onElementSelected]);

    // Visual Edit: Toggle selection mode in iframe when prop changes
    useEffect(() => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage({
                type: 'PIXEL_TOGGLE_SELECTION',
                enabled: selectionMode
            }, '*');
            console.log('[VisualEdit] Sent PIXEL_TOGGLE_SELECTION:', selectionMode);
        }
    }, [selectionMode]);

    // 1. Initialize Terminal
    useEffect(() => {
        if (!terminalRef.current || terminalInstance) return;

        // xterm exige des couleurs résolues (pas de var() CSS) : constantes des
        // deux thèmes OFFSET — fond surface-3, encre en light / papier en dark.
        const isDark = document.documentElement.classList.contains('dark');
        const term = new Terminal({
            convertEol: true,
            cursorBlink: true,
            theme: isDark
                ? { background: '#0C0C0B', foreground: '#F2F2EF', cursor: '#F2F2EF' }
                : { background: '#E7E7E3', foreground: '#161616', cursor: '#161616' },
            fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
        });

        const fitAddon = new FitAddon();
        term.loadAddon(fitAddon);

        term.open(terminalRef.current);
        fitAddon.fit();

        setTerminalInstance(term);
        fitAddonRef.current = fitAddon;

        // Handle resize
        const handleResize = () => fitAddon.fit();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            term.dispose();
        };
    }, []);

    // 2. Connect Terminal to Shell (JSH)
    useEffect(() => {
        if (!webContainer || !terminalInstance) return;

        let shellProcess: any = null;

        async function startShell() {
            if (!webContainer || !terminalInstance) return;

            const shell = await webContainer.spawn('jsh', {
                terminal: {
                    cols: terminalInstance.cols,
                    rows: terminalInstance.rows,
                },
            });

            shell.output.pipeTo(
                new WritableStream({
                    write(data) {
                        terminalInstance.write(data);
                    },
                })
            );

            const input = shell.input.getWriter();

            const dataDisposable = terminalInstance.onData((data) => {
                input.write(data);
            });

            shellProcess = shell;

            return () => {
                dataDisposable.dispose();
                // shell.kill(); // Ideally kill shell on unmount
            };
        }

        startShell();

        // Resize integration
        const resizeObserver = new ResizeObserver(() => {
            if (fitAddonRef.current) {
                fitAddonRef.current.fit();
                if (shellProcess) {
                    shellProcess.resize({
                        cols: terminalInstance.cols,
                        rows: terminalInstance.rows
                    });
                }
            }
        });

        if (terminalRef.current) {
            resizeObserver.observe(terminalRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [webContainer, terminalInstance]);


    // 3. Mount Config Files, Install Dependencies & Start Server (ONCE)
    useEffect(() => {
        if (!webContainer || isBooting) return;

        let installProcess: any = null;
        let devProcess: any = null;
        let isMounted = true;

        // Auto-retry state
        let retryCount = 0;
        const MAX_RETRIES = 3;

        const bootServer = async () => {
            console.log('[WebContainer] Booting server...');

            // 1. Prepare Config Files
            const configFileTree: Record<string, any> = {};

            // Generate package.json with deps
            configFileTree['package.json'] = {
                file: {
                    contents: JSON.stringify({
                        name: "pixel-app",
                        type: "module",
                        dependencies: {
                            "react": "^18.2.0",
                            "react-dom": "^18.2.0",
                            "react-router-dom": "^6.20.0",
                            "@heroicons/react": "^2.1.1", // Priority icon library
                            "lucide-react": "latest",
                            "clsx": "latest",
                            "tailwind-merge": "latest",
                            "class-variance-authority": "^0.7.0",
                            "@radix-ui/react-slot": "^1.0.2",
                            "framer-motion": "^10.16.0",
                            ...dependencies
                        },
                        devDependencies: {
                            "@vitejs/plugin-react": "^4.2.1",
                            "autoprefixer": "^10.4.18",
                            "postcss": "^8.4.35",
                            "tailwindcss": "^3.4.1",
                            "vite": "^5.1.4"
                        },
                        scripts: {
                            "dev": "vite",
                            "build": "vite build",
                            "preview": "vite preview"
                        }
                    }, null, 2)
                }
            };

            // Map static config files, injecting Visual Edit script into index.html
            Object.entries(CONFIG_FILES).forEach(([filename, content]) => {
                let fileContent = content;

                // Inject Visual Edit selection script into index.html
                if (filename === 'index.html') {
                    fileContent = content.replace(
                        '</body>',
                        `<script>${SELECTION_SCRIPT}</script>\n  </body>`
                    );
                }

                configFileTree[filename] = { file: { contents: fileContent } };
            });

            // Force strictPort: false in vite.config.js to handle port conflicts
            // We overwrite any existing vite.config.js or create a new one
            const viteConfigContent = `
import { defineConfig } from 'vite'
import react from '@vitejs/plugin-react'

// https://vitejs.dev/config/
export default defineConfig({
  plugins: [react()],
  server: {
    host: '0.0.0.0',
    port: 5173,
    strictPort: false, // Allow fallback to next available port
    hmr: {
        clientPort: 443 // Force client port for WebContainer environment
    }
  }
})
`;
            configFileTree['vite.config.js'] = { file: { contents: viteConfigContent } };

            // 2. Prepare Source Tree
            const initialSourceTree: Record<string, any> = {};
            initialSourceTree['src'] = {
                directory: {
                    'lib': { directory: {} },
                    'components': { directory: { 'ui': { directory: {} } } }
                }
            };

            // Map initial source files
            Object.entries(INITIAL_SRC_FILES).forEach(([path, content]) => {
                const parts = path.split('/'); // e.g. lib/utils.ts
                if (parts.length === 1) {
                    initialSourceTree['src'].directory[path] = { file: { contents: content } };
                } else {
                    const dir = parts[0];
                    const file = parts[1];
                    if (!initialSourceTree['src'].directory[dir]) initialSourceTree['src'].directory[dir] = { directory: {} };
                    initialSourceTree['src'].directory[dir].directory[file] = { file: { contents: content } };
                }
            });

            // Map Shadcn components
            Object.entries(SHADCN_COMPONENTS).forEach(([filename, content]) => {
                initialSourceTree['src'].directory['components'].directory['ui'].directory[filename] = {
                    file: { contents: content }
                };
            });

            console.log('[WebContainer] Mounting CONFIG and INITIAL files...');
            await webContainer.mount({ ...configFileTree, ...initialSourceTree });
            if (!isMounted) return;

            // Install Dependencies
            setIsInstalling(true);
            terminalInstance?.writeln('\x1b[33m→ Installation des dépendances…\x1b[0m');
            installProcess = await webContainer.spawn('npm', ['install']);
            if (!isMounted) { installProcess.kill(); return; }

            installProcess.output.pipeTo(new WritableStream({
                write(data) { terminalInstance?.write(data); }
            }));

            if ((await installProcess.exit) !== 0) {
                terminalInstance?.writeln("\x1b[31m✗ Échec de l'installation\x1b[0m");
                setIsInstalling(false);
                return;
            }
            setIsInstalling(false);
            terminalInstance?.writeln('\x1b[32m✔ Dépendances installées\x1b[0m');

            // Start Dev Server
            if (!serverStartedRef.current) {
                serverStartedRef.current = true;
                setIsStarting(true);
                terminalInstance?.writeln('\x1b[36m→ Démarrage du serveur…\x1b[0m');

                // CRITICAL: Register server-ready listener BEFORE spawning dev process
                // This prevents race condition where event fires before listener is registered
                webContainer.on('server-ready', (port: number, serverUrl: string) => {
                    if (!isMounted) return;
                    console.log('[WebContainer] Server ready at:', serverUrl);
                    urlRef.current = serverUrl;
                    setUrl(serverUrl);
                    setIsStarting(false);
                    setIsServerReady(true); // Mark server as ready
                    terminalInstance?.writeln(`\x1b[32m✔ Serveur prêt sur ${serverUrl}\x1b[0m`);
                    setActiveTab('preview');

                    // FIX: Double-refresh issue
                    // Vite sometimes emits server-ready before it's truly ready to serve the first request
                    // We force a reload after a short delay to ensure content loads
                    if (iframeRef.current) {
                        setTimeout(() => {
                            if (iframeRef.current && urlRef.current) {
                                console.log('[WebContainer] Triggering safety reload after server ready...');
                                iframeRef.current.src = urlRef.current;
                            }
                        }, 800);
                    }
                });

                devProcess = await webContainer.spawn('npm', ['run', 'dev']);
                if (!isMounted) { devProcess.kill(); return; }

                devProcess.output.pipeTo(new WritableStream({
                    write(data) {
                        terminalInstance?.write(data);

                        // Accumulate output in error buffer
                        errorBufferRef.current += data;

                        // Check for error patterns
                        const hasError = ERROR_PATTERNS.some(pattern => pattern.test(data));

                        // Special handling for Port Already in Use
                        if (data.includes('Port') && data.includes('already in use')) {
                            console.log('[WebContainer] Port conflict detected. Restarting with different port...');
                            setBuildError("Port conflict detected. Retrying...");
                            setTimeout(() => {
                                handleReload();
                            }, 1500);
                        }

                        if (hasError && !errorReportedRef.current && onBuildError) {
                            errorReportedRef.current = true;
                            // Extract relevant error message (last 50 lines of buffer)
                            const lines = errorBufferRef.current.split('\n');
                            const relevantLines = lines.slice(-50).join('\n');
                            const cleanError = relevantLines.replace(/\x1b\[[0-9;]*m/g, ''); // Clean ANSI codes

                            console.log('[WebContainer] Build error detected, calling onBuildError');
                            setBuildError(cleanError);

                            // Debounce to avoid multiple calls
                            setTimeout(() => {
                                onBuildError(cleanError);
                            }, 1000);
                        }

                        // Reset error reported when server is ready
                        if (data.includes('ready in') || data.includes('Local:')) {
                            errorReportedRef.current = false;
                            errorBufferRef.current = '';
                            setBuildError(null);
                        }

                        // Fallback URL detection from terminal output
                        // 1. Standard WebContainer URL
                        const urlMatch = data.match(/(https?:\/\/[^\s]+webcontainer-api\.io[^\s]*)/);
                        if (urlMatch && !urlRef.current) {
                            const detectedUrl = urlMatch[1].replace(/\x1b\[[0-9;]*m/g, ''); // Clean ANSI codes
                            console.log('[WebContainer] URL detected from terminal (fallback):', detectedUrl);
                            urlRef.current = detectedUrl;
                            setUrl(detectedUrl);
                            setIsStarting(false);
                            setActiveTab('preview');
                        }

                        // 2. Vite "Local:" output - signals server is up even if 'server-ready' didn't fire
                        // we can't extract the public URL from localhost here, but we can stop the spinner
                        // and let the user know they might need to reload if the frame is white
                        if (data.includes('Local:') && data.includes('http://localhost') && !urlRef.current) {
                            console.log('[WebContainer] Vite started (Local detected). Waiting for public URL...');
                            // We don't verify strict readiness here because we need the public URL via the event
                            // But we can reset the error state
                            setBuildError(null);
                        }
                    }
                }));
            }
        };

        bootServer();

        // Auto-Restart Watchdog (15s timeout)
        setTimeout(() => {
            if (isMounted && isStarting && !urlRef.current) {
                if (retryCount < MAX_RETRIES) {
                    console.log('[WebContainer] Server start timeout. Auto-restarting...');
                    retryCount++;
                    serverStartedRef.current = false; // Allow restart
                    bootServer();
                } else {
                    // Only show error if max retries exceeded
                    setBuildError("Le serveur ne répond pas. Veuillez recharger la page.");
                    setIsStarting(false);
                }
            }
        }, 15000);

        return () => {
            isMounted = false;
            // No references to installProcess/devProcess here if they aren't in scope?
            // They are defined at the top of useEffect, so they ARE in scope.
            // Wait, I need to check if I have access to clean them up.
            // Yes, let definitions are at top of effect.
            // But wait, the previous edit might have messed up the scope if braces were unbalanced.
            // Assuming braces are balanced now.

            // We need to kill processes if they exist
            // Note: installProcess and devProcess are let variables inside useEffect

            // However, since I can't easily access the *instances* if I didn't assign them to refs...
            // Ah, the code defined `let installProcess` at the top of useEffect.
            // So we can close over them.

            serverStartedRef.current = false;
            setIsStarting(false);
        };
    }, [webContainer]);







    // Effect 3b: Partial HMR / File Updates (Runs when files change from Props)
    // DEBOUNCED: Wait 500ms after last change to avoid overwhelming Vite during AI generation
    const wasGeneratingRef = useRef(false);

    useEffect(() => {
        if (!webContainer || isBooting) return;

        // Detect when generation just ended - force mount in this case
        const generationJustEnded = wasGeneratingRef.current && !isGenerating;
        wasGeneratingRef.current = isGenerating;

        // Skip HMR updates while AI is actively generating - will update when generation completes
        if (isGenerating) {
            console.log('[WebContainer] Skipping HMR - AI is generating');
            return;
        }

        // If generation just ended, log and proceed with immediate mount
        if (generationJustEnded) {
            console.log('[WebContainer] Generation just ended - forcing file mount');
        }

        // Cancel any pending debounced update
        if (debounceFilesRef.current) {
            clearTimeout(debounceFilesRef.current);
        }

        // Use shorter delay when generation just ended for immediate refresh
        // Otherwise use 500ms debounce to avoid overwhelming Vite during normal edits
        const debounceMs = generationJustEnded ? 50 : 500;

        debounceFilesRef.current = setTimeout(async () => {
            // STEP 1: Execute shell actions (npm install) BEFORE mounting source files
            // This prevents "package not found" errors during initial HMR compile
            // Note: We DON'T require isServerReady since npm install can run before dev server starts
            if (shellActions.length > 0 && webContainer) {
                // Helper to extract package names from npm install command (ignores versions/flags)
                const getPackageNames = nomsDePaquets;

                // Check if packages from a command are already installed
                const arePackagesInstalled = (cmd: string): boolean => {
                    const packages = getPackageNames(cmd);
                    if (packages.length === 0) return false;

                    // Check if any of the installed commands contain these packages
                    for (const executed of executedCommandsRef.current) {
                        const executedPackages = getPackageNames(executed);
                        if (packages.every(p => executedPackages.includes(p))) {
                            console.log('[WebContainer] Skipping duplicate install for packages:', packages.join(', '));
                            return true;
                        }
                    }
                    return false;
                };

                const newCommands = shellActions.filter(cmd => {
                    // Skip if exact command already executed
                    if (executedCommandsRef.current.has(cmd)) return false;
                    // Skip if packages already installed (handles version differences)
                    if (arePackagesInstalled(cmd)) return false;
                    return true;
                });
                for (const command of newCommands) {
                    // `webContainer.spawn` n'est PAS un shell : il lance directement le
                    // binaire avec la liste d'arguments. Un « && » n'y est donc pas un
                    // operateur — il etait transmis a npm comme un NOM DE PAQUET, avec
                    // « npm », « install » et « -D » a sa suite. La commande type
                    //   npm install react react-dom && npm install -D vite …
                    // echouait donc integralement (exit code 1), et l'apercu restait
                    // vide le temps que le serveur demarre sans ses dependances.
                    // Mesure du 25/09/2026 en production.
                    // On execute donc chaque sous-commande separement, dans l'ordre.
                    const sousCommandes = decouperSurEtEt(command);

                    let toutesReussies = true;

                    for (const sousCommande of sousCommandes) {
                        const { cmd, args } = enBinaireEtArguments(sousCommande);
                        if (!cmd) continue;

                        try {
                            console.log('[WebContainer] Executing shell action BEFORE mount:', sousCommande);
                            terminalInstance?.writeln(`\r\n\x1b[36m[Pixel] Exécution: ${sousCommande}\x1b[0m\r\n`);

                            const process = await webContainer!.spawn(cmd, args);
                            process.output.pipeTo(new WritableStream({
                                write(data) {
                                    terminalInstance?.write(data);
                                }
                            }));

                            const exitCode = await Promise.race([
                                process.exit,
                                new Promise<number>((_, reject) =>
                                    setTimeout(() => reject(new Error('Command timeout')), 60000)
                                )
                            ]);

                            if (exitCode === 0) {
                                console.log('[WebContainer] Shell action completed:', sousCommande);
                                terminalInstance?.writeln(`\r\n\x1b[32m✓ Commande terminée\x1b[0m\r\n`);
                            } else {
                                toutesReussies = false;
                                console.error('[WebContainer] Shell action failed:', sousCommande, 'exit code:', exitCode);
                                terminalInstance?.writeln(`\r\n\x1b[31m✗ Erreur (code: ${exitCode})\x1b[0m\r\n`);
                            }
                        } catch (err) {
                            toutesReussies = false;
                            console.error('[WebContainer] Shell action error:', sousCommande, err);
                            terminalInstance?.writeln(`\r\n\x1b[31m✗ Erreur: ${err}\x1b[0m\r\n`);
                        }
                    }

                    // La commande complete n'est retenue comme faite que si TOUTES ses
                    // sous-commandes ont abouti : sinon elle sera retentee au prochain
                    // passage, au lieu d'etre consideree a tort comme installee.
                    if (toutesReussies) {
                        executedCommandsRef.current.add(command);
                    }
                }
            }

            // STEP 2: Delete files that were removed
            if (deletedFiles && deletedFiles.length > 0) {
                for (const filePath of deletedFiles) {
                    let cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;

                    // Defensive: Skip paths that are clearly not files (shell operators, commands)
                    const invalidPaths = new Set(['&&', '||', ';', 'npm', 'npx', 'cd', 'ls', 'install', 'run', 'dev', 'build', 'start', 'rm']);
                    if (invalidPaths.has(cleanPath) || cleanPath.length < 2 || !/[./]/.test(cleanPath)) {
                        console.warn(`[WebContainer] Skipping invalid deletion path: "${cleanPath}"`);
                        continue;
                    }

                    let targetPath = cleanPath;

                    // Add src/ prefix logic (matching write logic)
                    if (!cleanPath.startsWith('src/') &&
                        !cleanPath.startsWith('public/') &&
                        !cleanPath.startsWith('components/')) {
                        if (cleanPath === 'App.tsx' || cleanPath === 'main.tsx' ||
                            cleanPath === 'styles.css' || cleanPath.endsWith('.tsx') ||
                            cleanPath.endsWith('.ts') || cleanPath.endsWith('.css')) {
                            targetPath = `src/${cleanPath}`;
                        }
                    }

                    try {
                        await webContainer.fs.rm(targetPath, { recursive: true, force: true });
                        console.log(`[WebContainer] Deleted: ${targetPath}`);
                        terminalInstance?.writeln(`\r\n\x1b[33mSupprimé: ${targetPath}\x1b[0m\r\n`);
                    } catch (err) {
                        // Ignore if file doesn't exist
                        console.log(`[WebContainer] Failed to delete ${targetPath} (may not exist)`);
                    }
                }
            }

            // STEP 3: Write source files individually using fs.writeFile (like bolt.diy)
            // This is more reliable for HMR than mount() which overwrites everything

            // Process each file and write it to the WebContainer filesystem
            for (const [path, content] of Object.entries(files)) {
                let cleanPath = path.startsWith('/') ? path.slice(1) : path;

                // Skip config files we manage ourselves
                if (cleanPath === 'package.json' || cleanPath === 'vite.config.js' ||
                    cleanPath === 'index.html' || cleanPath === 'tsconfig.json') continue;

                // CRITICAL: Add src/ prefix if not present (Vite expects src/ structure)
                let targetPath = cleanPath;
                if (!cleanPath.startsWith('src/') &&
                    !cleanPath.startsWith('public/') &&
                    !cleanPath.startsWith('components/')) {
                    // Files like App.tsx, main.tsx, styles.css go in src/
                    if (cleanPath === 'App.tsx' || cleanPath === 'main.tsx' ||
                        cleanPath === 'styles.css' || cleanPath.endsWith('.tsx') ||
                        cleanPath.endsWith('.ts') || cleanPath.endsWith('.css')) {
                        targetPath = `src/${cleanPath}`;
                    }
                }

                // Sanitize import paths (Hotfix for AI path errors)
                let fileContent = content;
                fileContent = fileContent
                    .replace(/from\s+['"]\.\.\/App(\.tsx)?['"]/g, 'from "./App"')
                    .replace(/from\s+['"]\.\.\/styles(\.css)?['"]/g, 'from "./styles.css"')
                    .replace(/import\s+['"]\.\.\/styles(\.css)?['"]/g, 'import "./styles.css"');

                // Create directory if needed
                const dir = targetPath.substring(0, targetPath.lastIndexOf('/'));
                if (dir) {
                    try {
                        await webContainer.fs.mkdir(dir, { recursive: true });
                    } catch (err) {
                        // Directory might already exist, that's fine
                    }
                }

                // Write the file
                try {
                    await webContainer.fs.writeFile(targetPath, fileContent);
                    console.log(`[WebContainer] Written: ${targetPath}`);
                } catch (err) {
                    console.error(`[WebContainer] Failed to write ${targetPath}:`, err);
                }
            }

            // If files are empty, don't proceed
            if (Object.keys(files).length === 0) return;

            console.log('[WebContainer] All source files written (HMR after ' + debounceMs + 'ms)');

            // Force iframe reload when generation just ended to ensure preview is up to date
            // This handles cases where HMR doesn't catch all changes
            if (generationJustEnded && iframeRef.current && urlRef.current) {
                console.log('[WebContainer] Generation ended - forcing iframe reload for fresh preview');
                // Small delay to let Vite process file changes before reload
                setTimeout(() => {
                    if (iframeRef.current && urlRef.current) {
                        iframeRef.current.src = urlRef.current + currentPath;
                    }
                }, 200);
            }
        }, debounceMs);

        return () => {
            if (debounceFilesRef.current) {
                clearTimeout(debounceFilesRef.current);
            }
        };
    }, [webContainer, files, isGenerating, shellActions, isServerReady, terminalInstance, deletedFiles]); // Include shell action deps

    // NOTE: Shell actions now execute BEFORE file mount in the HMR effect above
    // This prevents "package not found" errors during initial compile

    // White page detection: when AI finishes generating and we have a URL but no errors detected
    useEffect(() => {
        // Only check after AI has finished generating
        if (isGenerating || isAiLoading) return;
        if (!url || !isServerReady) return;
        if (buildError) return; // Already have an error

        // Wait 5 seconds after generation completes to check for white page
        const timeoutId = setTimeout(() => {
            // If we're still here with no errors, and the page might be blank
            // Check if we got a "ready" signal but isWhitePage is still false
            console.log('[WebContainer] Post-generation white page check');

            // Since we can't access iframe content due to cross-origin,
            // we rely on error patterns in terminal. If terminal shows no errors
            // but user sees white page, they can manually report it.

            // For now, we detect specific patterns that indicate React app loaded but is blank
            const terminalContent = errorBufferRef.current?.toLowerCase() || '';
            const hasReactBlankIndicators =
                terminalContent.includes('root render') && !terminalContent.includes('error') ||
                terminalContent.includes('hmr') && terminalContent.includes('no change');

            if (hasReactBlankIndicators && onBuildError) {
                console.log('[WebContainer] Potential white page detected via terminal patterns');
                setIsWhitePage(true);
            }
        }, 5000);

        return () => clearTimeout(timeoutId);
    }, [isGenerating, isAiLoading, url, isServerReady, buildError, onBuildError]);

    // Note: server-ready listener is now registered inside bootServer() to avoid race condition

    const handleReload = () => {
        if (iframeRef.current && url) {
            iframeRef.current.src = url + currentPath;
        }
    };

    // Navigate to a path within the preview
    const handleNavigate = (path: string) => {
        if (!url || !iframeRef.current) return;
        const cleanPath = path.startsWith('/') ? path : '/' + path;
        setCurrentPath(cleanPath);
        setUrlBarValue(cleanPath);
        iframeRef.current.src = url + cleanPath;
    };

    // Handle URL bar input
    const handleUrlBarSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleNavigate(urlBarValue);
    };

    // Navigation buttons
    const handleBack = () => {
        if (iframeRef.current) {
            // Try to go back in iframe history
            try {
                iframeRef.current.contentWindow?.history.back();
            } catch {
                // Cross-origin restriction, ignore
            }
        }
    };

    const handleForward = () => {
        if (iframeRef.current) {
            try {
                iframeRef.current.contentWindow?.history.forward();
            } catch {
                // Cross-origin restriction, ignore
            }
        }
    };

    // Étape courante du pipeline de génération — dérivée des états existants
    // (aucune nouvelle machine à états) : 0 analyse, 1 scaffold, 2 dépendances,
    // 3 build, 4 préview.
    const pipelineStep = url && isServerReady ? 4
        : isStarting ? 3
            : isInstalling ? 2
                : Object.keys(files).length > 0 ? 1
                    : 0;

    return (
        <div className="relative flex h-full w-full flex-col overflow-hidden bg-background">
            {/* Toolbar — onglets, URL et contrôles sur une seule ligne */}
            <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border bg-card px-3">
                {/* Onglets rectangulaires, mono 11 uppercase */}
                <div className="flex p-0.5">
                    <button
                        onClick={() => setActiveTab('preview')}
                        className={cn(
                            "flex items-center gap-1.5 px-2 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors",
                            activeTab === 'preview' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                        )}
                    >
                        <Monitor className="h-3 w-3" />
                        Préview
                    </button>
                    <button
                        onClick={() => setActiveTab('terminal')}
                        className={cn(
                            "flex items-center gap-1.5 px-2 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors",
                            activeTab === 'terminal' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                        )}
                    >
                        <TerminalIcon className="h-3 w-3" />
                        Terminal
                    </button>
                    <button
                        onClick={() => setActiveTab('code')}
                        className={cn(
                            "flex items-center gap-1.5 px-2 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors",
                            activeTab === 'code' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                        )}
                    >
                        <Code2 className="h-3 w-3" />
                        Code
                    </button>
                </div>

                {/* Séparateur */}
                <div className="h-4 w-px bg-border" />

                {/* Navigation + barre d'URL — uniquement en préview avec URL */}
                {activeTab === 'preview' && url && !isAiLoading ? (
                    <>
                        {/* Précédent / Suivant */}
                        <div className="flex items-center gap-0.5">
                            <button onClick={handleBack} className="p-1 text-faint transition-colors hover:bg-secondary hover:text-foreground" title="Précédent">
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button onClick={handleForward} className="p-1 text-faint transition-colors hover:bg-secondary hover:text-foreground" title="Suivant">
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        {/* Barre d'URL — mono, rectangulaire */}
                        <form onSubmit={handleUrlBarSubmit} className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 border border-border bg-secondary px-2 py-1">
                                <span className="max-w-[120px] truncate font-mono text-[11px] text-faint">
                                    {url?.replace(/^https?:\/\//, '').split('/')[0]?.slice(0, 20)}
                                </span>
                                <input
                                    type="text"
                                    value={urlBarValue}
                                    onChange={(e) => setUrlBarValue(e.target.value)}
                                    className="min-w-[40px] flex-1 bg-transparent font-mono text-[11px] text-foreground outline-none placeholder:text-faint"
                                    placeholder="/"
                                />
                            </div>
                        </form>

                        {/* Dropdown Pages */}
                        <div className="relative">
                            <button
                                onClick={() => setPagesDropdownOpen(!pagesDropdownOpen)}
                                className={cn(
                                    "flex items-center gap-1 border px-2 py-1 font-mono text-[11px] transition-colors",
                                    pagesDropdownOpen
                                        ? "border-hairline-strong bg-secondary text-foreground"
                                        : "border-border text-muted-foreground hover:text-foreground"
                                )}
                            >
                                Pages
                                {detectedPages.length > 1 && (
                                    <span className="bg-secondary px-1 py-0.5 font-mono text-[10px] text-muted-foreground">{detectedPages.length}</span>
                                )}
                                <ChevronDown className={cn("h-2.5 w-2.5 transition-transform", pagesDropdownOpen && "rotate-180")} />
                            </button>

                            {pagesDropdownOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setPagesDropdownOpen(false)} />
                                    <div className="absolute right-0 top-full z-20 mt-1 min-w-[160px] border border-border bg-popover py-1">
                                        <div className="mb-1 border-b border-border px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-faint">Pages</div>
                                        {detectedPages.map((page) => (
                                            <button
                                                key={page.path}
                                                onClick={() => { handleNavigate(page.path); setPagesDropdownOpen(false); }}
                                                className={cn(
                                                    "flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-[11px] transition-colors hover:bg-secondary",
                                                    currentPath === page.path && "bg-secondary"
                                                )}
                                            >
                                                <span className={currentPath === page.path ? "text-foreground" : "text-muted-foreground"}>{page.name}</span>
                                                <span className="font-mono text-[10px] text-faint">{page.path}</span>
                                            </button>
                                        ))}
                                        {detectedPages.length === 1 && (
                                            <div className="px-2 py-1.5 text-center text-[10px] text-faint">Une seule page détectée</div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Toggle viewport mobile / tablette / bureau */}
                        <div className="mx-1 h-4 w-px bg-border" />
                        <div className="flex items-center gap-0.5 border border-border p-0.5">
                            <button
                                onClick={() => setViewMode('mobile')}
                                className={cn(
                                    "p-1 transition-colors",
                                    viewMode === 'mobile' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                                )}
                                title="Mobile (375px)"
                            >
                                <Smartphone className="h-3 w-3" />
                            </button>
                            <button
                                onClick={() => setViewMode('tablet')}
                                className={cn(
                                    "p-1 transition-colors",
                                    viewMode === 'tablet' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                                )}
                                title="Tablette (768px)"
                            >
                                <Tablet className="h-3 w-3" />
                            </button>
                            <button
                                onClick={() => setViewMode('desktop')}
                                className={cn(
                                    "p-1 transition-colors",
                                    viewMode === 'desktop' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
                                )}
                                title="Bureau (100%)"
                            >
                                <Monitor className="h-3 w-3" />
                            </button>
                        </div>
                    </>
                ) : (
                    /* Indicateurs d'état quand l'URL n'est pas prête */
                    <div className="flex flex-1 items-center gap-2">
                        {isBooting && (
                            <span className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                                <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                                Initialisation…
                            </span>
                        )}
                        {isInstalling && (
                            <span className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                                <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                                Installation…
                            </span>
                        )}
                        {isStarting && (
                            <span className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                                <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                                Démarrage du serveur…
                            </span>
                        )}
                    </div>
                )}

                {/* Recharger + déploiement + version — toujours visibles */}
                <div className="ml-auto flex items-center gap-1.5">
                    <button onClick={handleReload} className="p-1 text-faint transition-colors hover:bg-secondary hover:text-foreground" title="Recharger">
                        <RotateCw className="h-3.5 w-3.5" />
                    </button>
                    {/* Deploy Status Popover */}
                    {projectId && (
                        <>
                            <div className="h-4 w-px bg-border" />
                            <DeployStatusPopover
                                projectId={projectId}
                                projectName={projectName || 'Mon Projet'}
                                files={Object.entries(files).map(([name, content]) => ({ name, content }))}
                            />
                        </>
                    )}
                    <span className="font-mono text-[10px] text-faint">v2.6</span>
                </div>
            </div>

            {/* Contenu — canvas surface-3, marge 24px, la frame est posée dessus */}
            <div className={cn(
                "relative flex-1 overflow-hidden bg-surface-3",
                activeTab === 'preview' ? '' : 'hidden'
            )}>
                {/* Onglet Préview */}
                <div className="flex h-full w-full flex-col items-center justify-center overflow-auto p-6">
                    {/* La frame : bg-card bordée hairline-strong, jamais collée bord à bord */}
                    <div
                        className={cn(
                            "relative overflow-hidden border border-hairline-strong bg-card",
                            viewMode === 'desktop' ? "h-full w-full" : "my-4",
                            viewMode === 'mobile' && "w-[375px] h-[667px]", // iPhone 8/SE dimensions as base
                            viewMode === 'tablet' && "w-[768px] h-[1024px]"  // iPad dimensions
                        )}
                        style={viewMode !== 'desktop' ? { maxHeight: 'calc(100% - 2rem)' } : {}}
                    >
                        {/* Show locked message when another tab has the preview */}
                        {isLocked ? (
                            <div className="flex h-full flex-col items-center justify-center bg-card">
                                <div className="flex max-w-sm flex-col items-center gap-6 px-6 text-center">
                                    <div className="flex h-12 w-12 items-center justify-center border border-border bg-secondary">
                                        <AlertTriangle className="h-5 w-5 text-warn" />
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="text-base font-medium text-foreground">Préview active ailleurs</h3>
                                        <p className="text-sm leading-relaxed text-muted-foreground">
                                            La préview est utilisée dans un autre onglet. Un seul onglet peut avoir la préview active.
                                        </p>
                                    </div>
                                    <button
                                        onClick={claimLock}
                                        className="border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                                    >
                                        Prendre le contrôle
                                    </button>
                                </div>
                            </div>
                        ) : isAiLoading ? (
                            /* Génération : pipeline vertical à connecteurs pointillés —
                               un plan de fabrication qu'on regarde s'exécuter. */
                            <div className="flex h-full flex-col items-center justify-center bg-card">
                                <div className="flex flex-col">
                                    <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                                        Génération
                                    </div>
                                    {PIPELINE_STEPS.map((label, i) => (
                                        <div key={label}>
                                            {i > 0 && (
                                                <div className="ml-[18px] h-3 border-l border-dashed border-border" />
                                            )}
                                            <div className="flex w-56 items-center gap-2.5 border border-border bg-card px-3 py-2">
                                                <span className="flex w-3 shrink-0 items-center justify-center">
                                                    {i === pipelineStep ? (
                                                        <PxMark state="generating" size={12} />
                                                    ) : i < pipelineStep ? (
                                                        <span className="h-1.5 w-1.5 bg-foreground" />
                                                    ) : (
                                                        <span className="h-1.5 w-1.5 bg-border" />
                                                    )}
                                                </span>
                                                <span className={cn(
                                                    "font-mono text-[10px] uppercase tracking-wider",
                                                    i <= pipelineStep ? "text-foreground" : "text-faint"
                                                )}>
                                                    {label}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : url ? (
                            <iframe
                                ref={iframeRef}
                                src={url}
                                className="h-full w-full border-0 bg-card"
                                title="Préview WebContainer"
                                allow="cross-origin-isolated"
                                onLoad={() => {
                                    // Clear any existing timeout
                                    if (whitePageTimeoutRef.current) {
                                        clearTimeout(whitePageTimeoutRef.current);
                                    }

                                    // Check for white page after a short delay
                                    whitePageTimeoutRef.current = setTimeout(() => {
                                        try {
                                            const iframe = iframeRef.current;
                                            if (iframe && iframe.contentDocument) {
                                                const body = iframe.contentDocument.body;
                                                const hasContent = body && (
                                                    body.innerHTML.trim().length > 100 ||
                                                    body.querySelector('*') !== null
                                                );

                                                if (!hasContent && !isAiLoading && onBuildError) {
                                                    console.log('[WebContainer] White page detected');
                                                    setIsWhitePage(true);
                                                    onBuildError('[PAGE BLANCHE] L\'application s\'est chargée mais n\'affiche rien. Vérifiez que le composant App retourne du JSX valide et que ReactDOM.render est correctement configuré.');
                                                } else {
                                                    setIsWhitePage(false);
                                                }
                                            }
                                        } catch (e) {
                                            // Cross-origin error - can't access iframe content, assume it's working
                                            setIsWhitePage(false);
                                        }
                                    }, 2000); // Wait 2s after load to check
                                }}
                            />
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center bg-card">
                                <PxMark state="thinking" size={24} className="mb-4" />
                                <p className="text-sm text-muted-foreground">Démarrage du serveur…</p>
                                <button
                                    onClick={() => setActiveTab('terminal')}
                                    className="mt-4 text-xs text-muted-foreground underline underline-offset-[3px] transition-colors hover:text-foreground"
                                >
                                    Voir les logs dans le terminal
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Onglet Terminal — surface-3, en phase avec le thème xterm */}
            <div className={cn(
                "min-h-0 w-full flex-1 bg-surface-3 p-2",
                activeTab === 'terminal' ? 'block' : 'hidden'
            )}>
                <div ref={terminalRef} className="h-full w-full" />
            </div>

            {/* Onglet Code */}
            <div className={cn(
                "min-h-0 w-full flex-1 bg-card",
                activeTab === 'code' ? 'flex' : 'hidden'
            )}>
                {/* Arborescence */}
                <div className="w-56 shrink-0 overflow-y-auto border-r border-border">
                    <div className="flex items-center justify-between border-b border-border p-2">
                        <div className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                            <FolderOpen className="h-3 w-3" />
                            Fichiers ({Object.keys(files).length})
                        </div>
                        {isAiLoading && (
                            <div className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-ok" />
                                <span className="font-mono text-[10px] uppercase tracking-wider text-ok">En direct</span>
                            </div>
                        )}
                    </div>
                    <div className="py-1">
                        {Object.keys(files).length === 0 ? (
                            <div className="px-3 py-2 text-xs text-faint">Aucun fichier</div>
                        ) : (
                            // Group files by directory
                            (() => {
                                const tree: Record<string, string[]> = { root: [] };
                                Object.keys(files).forEach(filePath => {
                                    const parts = filePath.replace(/^\//, '').split('/');
                                    if (parts.length === 1) {
                                        tree.root.push(filePath);
                                    } else {
                                        const dir = parts.slice(0, -1).join('/');
                                        if (!tree[dir]) tree[dir] = [];
                                        tree[dir].push(filePath);
                                    }
                                });

                                // Icônes lucide sobres par type de fichier (plus d'emojis)
                                const getFileIcon = (path: string) => {
                                    if (path.endsWith('.tsx') || path.endsWith('.ts') || path.endsWith('.html')) return FileCode;
                                    if (path.endsWith('.css') || path.endsWith('.json')) return Braces;
                                    return FileText;
                                };

                                return (
                                    <>
                                        {/* Directories */}
                                        {Object.keys(tree).filter(k => k !== 'root').sort().map(dir => (
                                            <div key={dir}>
                                                <div className="flex items-center gap-1 px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-faint">
                                                    <ChevronRight className="h-2.5 w-2.5" />
                                                    <Folder className="h-3 w-3" />
                                                    {dir}
                                                </div>
                                                {tree[dir].map(filePath => {
                                                    const FileIcon = getFileIcon(filePath);
                                                    return (
                                                        <button
                                                            key={filePath}
                                                            onClick={() => setSelectedFile(filePath)}
                                                            className={cn(
                                                                "flex w-full items-center gap-2 border-l-2 py-1 pl-4 pr-3 text-left text-xs transition-colors",
                                                                selectedFile === filePath
                                                                    ? "border-foreground bg-secondary text-foreground"
                                                                    : "border-transparent text-muted-foreground hover:bg-secondary"
                                                            )}
                                                        >
                                                            <FileIcon className="h-3 w-3 shrink-0 text-faint" />
                                                            <span className="truncate font-mono">{filePath.split('/').pop()}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                        {/* Root files */}
                                        {tree.root.map(filePath => {
                                            const FileIcon = getFileIcon(filePath);
                                            return (
                                                <button
                                                    key={filePath}
                                                    onClick={() => setSelectedFile(filePath)}
                                                    className={cn(
                                                        "flex w-full items-center gap-2 border-l-2 py-1 pl-3 pr-3 text-left text-xs transition-colors",
                                                        selectedFile === filePath
                                                            ? "border-foreground bg-secondary text-foreground"
                                                            : "border-transparent text-muted-foreground hover:bg-secondary"
                                                    )}
                                                >
                                                    <FileIcon className="h-3 w-3 shrink-0 text-faint" />
                                                    <span className="truncate font-mono">{filePath.replace(/^\//, '')}</span>
                                                </button>
                                            );
                                        })}
                                    </>
                                );
                            })()
                        )}
                    </div>
                </div>

                {/* Viewer — coloration monochrome : graisse et opacité, pas de couleurs */}
                <div className="flex-1 overflow-auto">
                    {selectedFile && files[selectedFile] ? (
                        <div className="flex h-full flex-col">
                            <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2">
                                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                                    <FileCode className="h-3 w-3 text-faint" />
                                    {selectedFile.replace(/^\//, '')}
                                    {isAiLoading && (
                                        <span className="ml-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-ok">
                                            <span className="h-1.5 w-1.5 rounded-full bg-ok" />
                                            En direct
                                        </span>
                                    )}
                                </div>
                                <span className="font-mono text-[10px] text-faint">
                                    {files[selectedFile].split('\n').length} lignes
                                </span>
                            </div>
                            <pre className="flex-1 overflow-auto p-4 font-mono text-xs leading-relaxed">
                                <code className="block">
                                    {files[selectedFile].split('\n').map((line, i) => (
                                        <div key={i} className="flex">
                                            <span className="w-8 shrink-0 select-none pr-3 text-right text-faint">{i + 1}</span>
                                            <span className={cn(
                                                line.trim().startsWith('//') || line.trim().startsWith('/*') ? 'text-faint' :
                                                    line.trim().startsWith('import') || line.trim().startsWith('export') ? 'text-muted-foreground' :
                                                        line.includes('function') || line.includes('const') || line.includes('return') ? 'font-medium text-foreground' :
                                                            'text-foreground'
                                            )}>{line || ' '}</span>
                                        </div>
                                    ))}
                                </code>
                            </pre>
                        </div>
                    ) : (
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                            <div className="text-center">
                                <Code2 className="mx-auto mb-2 h-8 w-8 text-faint" />
                                <p>Sélectionnez un fichier pour voir le code</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Overlay d'erreur — backdrop encre 40 %, sans blur */}
            {pendingError && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
                    <div className="max-w-md w-full">
                        <ErrorBanner
                            error={pendingError}
                            onTryFix={onTryFix}
                            onDismiss={onDismissError}
                            isFixing={isFixingError}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export const WebContainerPreview = memo(WebContainerPreviewComponent, (prevProps, nextProps) => {
    // Only re-render if fundamental props change avoiding heavy iframe reloads
    // files reference check (assuming parent uses state properly)
    // isGenerating / isLoading checked

    return (
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.isGenerating === nextProps.isGenerating &&
        prevProps.files === nextProps.files && // Reference equality should be enough if using React state
        prevProps.selectionMode === nextProps.selectionMode &&
        prevProps.onElementSelected === nextProps.onElementSelected &&
        prevProps.pendingError === nextProps.pendingError
    );
});
